import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getAllGroups } from "../../../../services/groupServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_FILTERS,
  buildGroupStats,
  filterGroups,
  getUniqueCoaches,
  paginateItems,
  sortGroups,
} from "../utils/adminGroupsUtils";

export function useAdminGroups() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchGroups = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getAllGroups();
      const safeGroups = Array.isArray(data) ? data : [];

      setGroups(safeGroups);

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Groupes actualisés.", "success");
      }
    } catch (error) {
      console.error("Erreur récupération groupes :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des groupes.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups({ silent: true });
  }, [fetchGroups]);

  const filteredGroups = useMemo(() => {
    return sortGroups(filterGroups(groups, filters), filters.sort);
  }, [filters, groups]);

  const pagination = useMemo(() => {
    return paginateItems(filteredGroups, page, filters.perPage);
  }, [filteredGroups, filters.perPage, page]);

  const stats = useMemo(() => {
    return buildGroupStats(groups);
  }, [groups]);

  const coachOptions = useMemo(() => {
    return getUniqueCoaches(groups);
  }, [groups]);

  const updateFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const openGroupDrawer = useCallback((group) => {
    setSelectedGroup(group);
    setIsDrawerOpen(true);
  }, []);

  const closeGroupDrawer = useCallback(() => {
    setSelectedGroup(null);
    setIsDrawerOpen(false);
  }, []);

  return {
    groups,
    filteredGroups,
    paginatedGroups: pagination.items,
    filters,
    stats,
    coachOptions,
    selectedGroup,
    isDrawerOpen,
    loading,
    refreshing,
    page: pagination.page,
    totalPages: pagination.totalPages,
    fetchGroups,
    updateFilter,
    resetFilters,
    setPage,
    openGroupDrawer,
    closeGroupDrawer,
  };
}